import React from 'react'

function PrivacyPolicyPage() {
  const lastUpdated = 'October 27, 2025'

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px', lineHeight: 1.6 }}>
      <h1 className='mt-5' style={{ marginBottom: 8 }}>Privacy Policy</h1>
      <div style={{ color: '#666', marginBottom: 24 }}>Last updated: {lastUpdated}</div>

      <p>
        This Privacy Policy explains how Kipps Rajendra Nagar ("Kipps Rajendra Nagar", "we", "our", or "us")
        collects, uses, shares, and protects your information when you use the Kipps Rajendra Nagar mobile application
        and website (collectively, the "Services"). By using the Services, you agree to the collection and use of
        information in accordance with this Privacy Policy.
      </p>

      <h2>Information We Collect</h2>
      <p>We collect information to provide and improve the Services. The information we collect includes:</p>
      <ul>
        <li>
          <b>Account and Contact Information:</b> Your mobile number, name, and any profile details you choose to provide.
        </li>
        <li>
          <b>Address and Delivery Details:</b> Recipient name, phone number, house/flat number, area, city, landmark, and
          optional location coordinates you choose to provide to improve delivery accuracy.
        </li>
        <li>
          <b>Order Information:</b> Items added to cart, placed orders, order totals, discounts/offers applied, and timestamps.
        </li>
        <li>
          <b>Payment Information:</b> When you make a payment using PhonePe or other integrated payment gateways, we may collect transaction details (such as payment status, transaction ID, and amount). <br /> <b>Note:</b> We do not store your card, UPI PIN, or sensitive payment credentials. All payment processing is handled securely by PhonePe or the respective payment provider.
        </li>
        <li>
          <b>Communications:</b> OTP messages for login/verification and your communications with support.
        </li>
        <li>
          <b>Usage and Device Information:</b> Basic technical details such as IP address, device type, and browser/app version
          that may be logged by our servers or hosting providers for security and diagnostics.
        </li>
        <li>
          <b>Cookies and Similar Technologies (Website):</b> Cookies may be used on our website to enable core functionality
          (e.g., session management) and for performance and analytics where applicable.
        </li>
      </ul>

      <h2>How We Use Your Information</h2>
      <ul>
        <li>To create and manage your account and authenticate you via OTP.</li>
        <li>To process orders, arrange delivery, and provide customer support.</li>
        <li>To improve, personalize, and expand our Services and user experience.</li>
        <li>To prevent fraud, abuse, and enforce our terms and policies.</li>
        <li>To comply with legal obligations and regulatory requirements.</li>
      </ul>

      <h2>Information Sharing and Disclosure</h2>
      <p>We do not sell your personal information. We may share information in the following limited circumstances:</p>
      <ul>
        <li>
          <b>Service Providers:</b> With trusted vendors who perform services for us, such as hosting, messaging/OTP delivery
          providers, payment gateway partners (including PhonePe), and operational support. These providers are given access only as necessary to perform their
          functions and are obligated to protect your information.
        </li>
        <li>
          <b>Payment Gateway Integration:</b> When you make payments via PhonePe, your payment data is processed directly by PhonePe according to their privacy and security policies. We do not store your sensitive payment credentials. We may receive transaction status and reference IDs for order tracking and support.
        </li>
        <li>
          <b>Legal and Safety:</b> If required by law or in response to valid legal requests, or to protect the rights,
          property, or safety of Kipps Rajendra Nagar, our users, or the public.
        </li>
        <li>
          <b>Business Transfers:</b> In connection with a merger, acquisition, or asset sale, your information may be
          transferred as permitted by law, subject to this Policy or a successor policy with equivalent protections.
        </li>
      </ul>

      <h2>Data Retention</h2>
      <p>
        We retain your personal information for as long as your account is active or as needed to provide the Services
        and fulfill the purposes outlined in this Policy. We may also retain certain information for a reasonable period
        after account deletion to comply with legal, tax, or accounting obligations, resolve disputes, and enforce our
        agreements.
      </p>

      <h2>Security</h2>
      <p>
        We use reasonable administrative, technical, and physical safeguards designed to protect your information.
        However, no method of transmission over the internet or method of electronic storage is 100% secure, and we
        cannot guarantee absolute security.
      </p>

      <h2>Your Choices and Rights</h2>
      <ul>
        <li>
          Access, Update, or Correct: You may update your account details and addresses within the app. For other
          requests, contact us at <a href="mailto:info@kipps.in">info@kipps.in</a>.
        </li>
        <li>
          Account Deletion: You can delete your account from the mobile app by navigating to Profile → Delete Account.
          This will permanently remove your account. Certain order or transactional records may be retained as required
          by law.
        </li>
        <li>
          Communications: You may control certain communications (e.g., marketing) where provided. OTP messages for
          login are transactional and cannot be opted out of while using the app.
        </li>
      </ul>

      <h2>Children’s Privacy</h2>
      <p>
        Our Services are not directed to children under the age of 13. We do not knowingly collect personal information
        from children under 13. If you believe a child has provided us with personal information, please contact us and
        we will take appropriate steps to delete such information.
      </p>

      <h2>International Data Transfers</h2>
      <p>
        Your information may be processed and stored in countries other than your own. Where required, we take steps to
        ensure appropriate protections for such transfers in accordance with applicable law.
      </p>

      <h2>Third-Party Services and SDKs</h2>
      <p>
        We may use third-party services to operate and improve our Services (for example, cloud hosting, SMS gateways
        for OTP delivery, and payment gateway SDKs such as PhonePe). These third parties process your information on our behalf and are bound by confidentiality
        and data protection obligations. We do not allow these providers to sell your personal data. For payments, you are subject to the privacy policy and terms of the respective payment provider (e.g., PhonePe).
      </p>

      <h2>Cookies and Tracking (Website)</h2>
      <p>
        Our website may use cookies to enable core functionality and improve performance. You can control cookies
        through your browser settings. The mobile app does not use browser cookies.
      </p>

      <h2>Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. The updated version will be indicated by an updated
        “Last updated” date and will be effective as soon as it is accessible. We encourage you to review this Policy
        periodically to stay informed about our practices.
      </p>

      <h2>Contact Us</h2>
      <p>
        If you have any questions or concerns about this Privacy Policy or the data practices of Kipps Rajendra Nagar,
        please contact us at:
      </p>
      <ul>
        <li>Email: <a href="mailto:info@kipps.in">info@kipps.in</a></li>
        <li>Support Phone (India): +91 9068613333</li>
      </ul>

      <hr style={{ margin: '24px 0' }} />
      <p style={{ color: '#666', fontSize: 14 }}>
        Note: This policy is intended to meet Google Play’s requirement to host a publicly accessible privacy policy URL
        that clearly discloses collection, use, and sharing of personal and sensitive user data. Please ensure any
        payment providers, analytics tools, or additional SDKs you use are reflected here.
      </p>
    </div>
  )
}

export default PrivacyPolicyPage